import React, { useEffect, useState, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import {
  ChartBarIcon,
  DocumentIcon,
  CalendarIcon,
  UserIcon,
  SpinnerIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../components/icons";
import {
  getOfferAnalyticsSummary,
  getOfferAnalyticsSessions,
  getOfferDetails,
  getOfferSectionTimeAnalytics,
  getSessionSectionTimeDetails,
  getOfferVersions,
  type OfferVersion,
} from "../services/api";
import {
  OfferAnalyticsSummary,
  OfferAnalyticsSessionsResponse,
  Offer,
  OfferSectionTimeAnalyticsResponse,
  SessionSectionTime,
} from "../types";
import { useToast } from "../contexts/ToastContext";
import SectionTimeAnalytics from "../components/SectionTimeAnalytics";
import SessionSectionTimeDetails from "../components/SessionSectionTimeDetails";
import OfferVersionSelector from "../components/OfferVersionSelector";

const OfferAnalytics: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [summary, setSummary] = useState<OfferAnalyticsSummary | null>(null);
  const [sessions, setSessions] =
    useState<OfferAnalyticsSessionsResponse | null>(null);
  const [sectionAnalytics, setSectionAnalytics] =
    useState<OfferSectionTimeAnalyticsResponse | null>(null);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(
    new Set()
  );
  const [sessionDetails, setSessionDetails] = useState<
    Map<string, SessionSectionTime[]>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [sectionAnalyticsLoading] = useState(false); // setSectionAnalyticsLoading currently unused
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "sessions">(
    "overview"
  );
  const { addToast } = useToast();

  // Version management
  const [versions, setVersions] = useState<OfferVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );

  // Fetch versions on mount
  useEffect(() => {
    const fetchVersions = async () => {
      if (!offerId) return;

      try {
        const versionsData = await getOfferVersions(offerId);
        setVersions(versionsData);
      } catch (error) {
        console.error("Error fetching versions:", error);
        // Don't fail - versions are optional
      }
    };

    fetchVersions();
  }, [offerId]);

  // Fetch analytics data (depends on selectedVersionId)
  useEffect(() => {
    const fetchData = async () => {
      if (!offerId) return;

      try {
        setLoading(true);

        // Use selectedVersionId if available, otherwise use offerId
        const targetId = selectedVersionId || offerId;

        // Fetch offer details, analytics summary, sessions, and section analytics in parallel
        const [offerData, summaryData, sessionsData, sectionData] =
          await Promise.all([
            getOfferDetails(offerId), // Always fetch offer details from main ID
            getOfferAnalyticsSummary(targetId),
            getOfferAnalyticsSessions(targetId),
            getOfferSectionTimeAnalytics(targetId).catch(() => null), // Don't fail if section analytics unavailable
          ]);

        setOffer(offerData);
        setSummary(summaryData);
        setSessions(sessionsData);
        setSectionAnalytics(sectionData);
      } catch (err) {
        console.error("Error fetching analytics data:", err);
        setError("Nie udało się załadować danych analitycznych");
        addToast({
          message: "Błąd podczas ładowania analityki",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [offerId, selectedVersionId, addToast]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const formatDate = (dateString: string | Date | null): string => {
    if (!dateString) return "Nigdy";
    const date = new Date(dateString);
    return date.toLocaleString("pl-PL", {
      timeZone: "Europe/Warsaw",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleSessionExpansion = async (sessionId: string) => {
    if (!offerId) return;

    const newExpanded = new Set(expandedSessions);

    if (expandedSessions.has(sessionId)) {
      newExpanded.delete(sessionId);
      setExpandedSessions(newExpanded);
    } else {
      newExpanded.add(sessionId);
      setExpandedSessions(newExpanded);

      // Load session section details if not already loaded
      if (!sessionDetails.has(sessionId)) {
        try {
          // Use selectedVersionId if available
          const targetId = selectedVersionId || offerId;
          const details = await getSessionSectionTimeDetails(
            targetId,
            sessionId
          );
          setSessionDetails((prev) => new Map(prev).set(sessionId, details));
        } catch (error) {
          console.error("Error loading session section details:", error);
          addToast({
            message: "Błąd podczas ładowania szczegółów sesji",
            type: "error",
          });
        }
      }
    }
  };

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex justify-center items-center py-20">
          <SpinnerIcon className="h-8 w-8 text-primary-600" />
          <span className="ml-2 text-lg text-neutral-600">
            Ładowanie analityki...
          </span>
        </div>
      </Layout>
    );
  }

  if (error || !offer || !summary) {
    return (
      <Layout showBackButton={true}>
        <div className="text-center py-20 card">
          <div className="mx-auto h-12 w-12 bg-error-100 rounded-full flex items-center justify-center mb-4">
            <ChartBarIcon className="h-6 w-6 text-error-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Błąd ładowania analityki
          </h3>
          <p className="mt-1 text-sm text-neutral-600">{error}</p>
          <div className="mt-6">
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <Link
            to="/offers"
            className="text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-neutral-900">
              Analityka oferty
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              {offer.title} • {offer.clientName}
            </p>
          </div>
        </div>

        {/* Version Selector */}
        {versions.length > 0 && (
          <div className="mb-4">
            <OfferVersionSelector
              versions={versions}
              currentOfferId={selectedVersionId || offerId || ""}
              currentPublicToken={offer.publicToken}
              currentPublishedDate={null}
              onVersionChange={(versionId) => {
                setSelectedVersionId(versionId);
              }}
            />
          </div>
        )}

        {/* Quick stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-neutral-600">
                  Wyświetlenia
                </p>
                <p className="text-2xl font-semibold text-neutral-900">
                  {summary.view_count}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserIcon className="h-8 w-8 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-neutral-600">
                  Unikalne sesje
                </p>
                <p className="text-2xl font-semibold text-neutral-900">
                  {summary.unique_sessions}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-neutral-600">
                  Sumaryczny czas na ofercie
                </p>
                <p className="text-2xl font-semibold text-neutral-900">
                  {formatDuration(summary.total_session_duration)}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-info-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-neutral-600">
                  Ostatnie wyświetlenie
                </p>
                <p className="text-sm font-semibold text-neutral-900">
                  {formatDate(summary.last_viewed_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("overview")}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === "overview"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
            }`}
          >
            Przegląd
          </button>
          <button
            onClick={() => setActiveTab("sessions")}
            className={`py-2 px-1 border-b-2 text-sm font-medium ${
              activeTab === "sessions"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300"
            }`}
          >
            Sesje ({sessions?.total_count || 0})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Statystyki podstawowe
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    Całkowity czas sesji:
                  </span>
                  <span className="text-sm font-medium text-neutral-900">
                    {formatDuration(summary.total_session_duration)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    Średni czas sesji:
                  </span>
                  <span className="text-sm font-medium text-neutral-900">
                    {formatDuration(summary.average_session_duration)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-600">
                    Dane analityczne:
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      summary.has_analytics
                        ? "text-success-600"
                        : "text-neutral-500"
                    }`}
                  >
                    {summary.has_analytics ? "Dostępne" : "Brak danych"}
                  </span>
                </div>
              </div>
            </div>

            <SectionTimeAnalytics
              sectionAnalytics={sectionAnalytics?.sectionAnalytics || []}
              totalOfferViewTime={sectionAnalytics?.totalOfferViewTime || 0}
              loading={sectionAnalyticsLoading}
            />
          </div>

          {!summary.has_analytics && (
            <div className="card p-6 text-center">
              <ChartBarIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Brak danych analitycznych
              </h3>
              <p className="text-sm text-neutral-600">
                Ta oferta nie ma jeszcze żadnych sesji analitycznych. Dane
                pojawią się po pierwszych wyświetleniach przez klientów.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "sessions" && (
        <div className="card">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900">
              Sesje użytkowników
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              Szczegółowe informacje o sesjach przeglądania oferty
            </p>
          </div>

          {sessions && sessions.sessions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Sesja
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Czas trwania
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Urządzenie
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Przeglądarka
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Zgodna
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {sessions.sessions.map((session, index) => (
                    <Fragment key={session.session_id}>
                      <tr className="hover:bg-neutral-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              onClick={() =>
                                toggleSessionExpansion(session.session_id)
                              }
                              className="mr-2 p-1 hover:bg-neutral-100 rounded"
                            >
                              <ChevronRightIcon
                                className={`h-4 w-4 text-neutral-400 transition-transform ${
                                  expandedSessions.has(session.session_id)
                                    ? "rotate-90"
                                    : ""
                                }`}
                              />
                            </button>
                            <div>
                              <div className="text-sm text-neutral-900">
                                {formatDate(session.started_at)}
                              </div>
                              <div className="text-xs text-neutral-500">
                                {session.session_id.slice(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                          {formatDuration(session.total_duration)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-neutral-900">
                            {session.device_type}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {session.screen_resolution}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-neutral-900">
                            {session.browser_name} {session.browser_version}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {session.operating_system}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              session.consent_analytics
                                ? "bg-success-100 text-success-800"
                                : "bg-warning-100 text-warning-800"
                            }`}
                          >
                            {session.consent_analytics ? "Tak" : "Nie"}
                          </span>
                        </td>
                      </tr>

                      {/* Expanded session details */}
                      {expandedSessions.has(session.session_id) && (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 bg-neutral-50">
                            <SessionSectionTimeDetails
                              sessionId={session.session_id}
                              sectionTimes={
                                sessionDetails.get(session.session_id) || []
                              }
                              totalSessionDuration={session.total_duration}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <UserIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Brak sesji
              </h3>
              <p className="text-sm text-neutral-600">
                Ta oferta nie ma jeszcze żadnych zapisanych sesji.
              </p>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default OfferAnalytics;
